import { useState } from 'react'
import { PaymentResultByCash, PaymentResultByCredit, SelfCheckInState } from '@/models/self-checkin'
import { GetSelfCheckinSelfCheckinBasicInfoResponse } from '@/models/self-checkin/plugin'
import { Guide } from '@/utils/guide'
import { checkActivePlugin, PluginCode } from '@/libs/plugins'
import * as api from '@/apis/aipass'
import dayjs from 'dayjs'
import { isAccommodationInput, isEmployee, OccupationType } from '@/models/self-checkin/accommodation-info'
import { DeviceType, OutputCompleteEvent } from '@/apis/vescajs'
import {
  cashChangerStorageKey,
  checkinEposPrinterIdStorage,
  serialNumberStorageKey,
  terminalPortStorageKey,
  terminalStorageKey,
} from '@/constants/self-checkin-device-storage'
import { PaymentMethod, PaymentMethodInput } from '@/models/payment-method'
import { PaymentTerminalSettingType } from '@/models/payment-setting'
import { signout } from '@/apis/auth0'
import { useHistory } from 'react-router-dom'
import { reportError } from '@/components/apps/error-boundary'
import { RoomOptionDisplayType } from '@/models/self-checkin/setting'
import { pickUploadReservationUuid } from '@/utils/reservations'
import { buildJapaneseIdentityDocumentImages } from '@/libs/self-checkin'

type RequiredOnly<T, U extends keyof T> = T & Required<Pick<T, U>>

export type SuccessMailType = {
  hotelId: string
  checkinId: string
  checkinMemo: string
  checkinDateTime: string
  guestEmail: string | undefined
  guestName: string | undefined

  reception?: {
    number: string
    date: string
    paymentMethod: string
    paymentMethodEn?: string
    paymentAmount: string
    name: string
    paymentMethods?: PaymentMethodInput[]
    language: string
    reservations: Array<{
      useAmount: number
      paymentAmount: number
      internalDiscount: number
    }>
  }
  assignSpaces?: Array<{
    usageFrom: string // YYYY-MM-DD HH:MM:DD
    usageTo: string // YYYY-MM-DD HH:MM:DD
    spaceId: string
    reservationUserName: string
    pax: number
    reservationId: string
    timeSelectionId: string
  }>
  reservationIdWithAssignIds: Array<{
    reservationId: string
    guestRoomAssignIds: string[]
  }>
  hasPrinter?: boolean
}

export const useSelfCheckInState = () => {
  const history = useHistory()
  const [state, setState] = useState<SelfCheckInState>(JSON.parse(sessionStorage.getItem('selfcheckinstate') || '{}'))

  const saveState = (state: SelfCheckInState) => {
    sessionStorage.setItem('selfcheckinstate', JSON.stringify(state))
    setState(state)
  }

  const clearState = (hotelId: string) => {
    sessionStorage.setItem('selfcheckinstate', JSON.stringify({ hotelId }))
    // hotelIdだけを引き継ぎ強制クリアを実行
    setState({ hotelId } as any)
  }

  const initializeState = (
    hotelId: string,
    setting: {
      basicInfo: GetSelfCheckinSelfCheckinBasicInfoResponse['results']
      customCheckin: Array<Guide>
      payment: Array<PaymentMethod>
      paymentTerminal: PaymentTerminalSettingType
      // 設定のレコードが無い施設では取得 API が設定値を返さないため、各項目を省略可能で受ける（下の処理は実際にある項目だけを写す）
      selfCheckinSetting:
        | Partial<{
            requiredAccompanyNameInput: number | boolean
            requiredAddressInput: number | boolean
            requiredBirthDateInput: number | boolean
            requiredEmailInput: number | boolean
            requiredIdentifyVerification: number | boolean
            requiredForeignIdentify: number | boolean
            requiredFuriganaInput: number | boolean
            requiredGenderInput: number | boolean
            requiredIdentify: number | boolean
            requiredTelephoneInput: number | boolean
            isFrontDescGuidance: number | boolean
            isCheckinAllowedBeforeRoomInspection: number | boolean
            roomOptionDisplayType: RoomOptionDisplayType
          }>
        | undefined
    },
  ): SelfCheckInState => {
    const castSelfCheckinSetting = {} as any
    if (setting.selfCheckinSetting) {
      Object.keys(setting.selfCheckinSetting).forEach(k => (castSelfCheckinSetting![k] = !!setting.selfCheckinSetting![k]))
      if (castSelfCheckinSetting?.requiredIdentify !== undefined) {
        Object.assign(castSelfCheckinSetting, {
          requiredJapaneseIdentify: castSelfCheckinSetting.requiredIdentify,
        })
      }
    }
    const hasPaymentPlugin = checkActivePlugin(setting.basicInfo.plugin, PluginCode.Payment)
    const hasPaymentTerminalPlugin = checkActivePlugin(setting.basicInfo.plugin, PluginCode.PaymentTerminal)
    const storedTerminalIp = localStorage.getItem(terminalStorageKey) || ''
    const storedCashChangerIp = localStorage.getItem(cashChangerStorageKey) || ''
    return {
      _type: 'selfCheckIn',
      hotelId,
      setting: {
        ...setting.basicInfo,
        basicInfo: setting.basicInfo.basicInfo[0],
        customCheckin: setting.customCheckin,
        accommodation: {
          requiredJapaneseIdentify: true,
          requiredForeignIdentify: true,
          requiredIdentifyVerification: false,
          requiredAccompanyNameInput: false,
          requiredFuriganaInput: false,
          requiredBirthDateInput: false,
          requiredGenderInput: false,
          requiredTelephoneInput: false,
          requiredEmailInput: false,
          requiredAddressInput: false,
          isFrontDescGuidance: false,
          isCheckinAllowedBeforeRoomInspection: true,
          ...castSelfCheckinSetting,
          roomOptionDisplayType: setting.selfCheckinSetting?.roomOptionDisplayType || RoomOptionDisplayType.RoomAssignName,
        },
        payment: hasPaymentPlugin ? setting.payment : [],
        paymentTerminal: setting.paymentTerminal,
        isEnabledPaymentTerminal: storedTerminalIp.length > 0 && hasPaymentTerminalPlugin,
        isEnabledCashChanger: storedCashChangerIp.length > 0 && hasPaymentTerminalPlugin,
      },
    }
  }

  const doCheckIn = async (params: {
    state: SelfCheckInState
    language: string
    guestAppUrl: any
    selectedPaymentMethod?: PaymentMethod
    paymentResult?: (PaymentResultByCredit | PaymentResultByCash) & {
      deviceType?: DeviceType
      salesSlip?: OutputCompleteEvent['pmsResult']['salesSlip']
    }
  }) => {
    api.stateLog({
      ...params.state,
      localStorage: {
        serialNumberStorageKey: localStorage.getItem(serialNumberStorageKey),
        terminalPortStorageKey: localStorage.getItem(terminalPortStorageKey),
        terminalStorageKey: localStorage.getItem(terminalStorageKey),
        checkinEposPrinterIdStorage: localStorage.getItem(checkinEposPrinterIdStorage),
        cashChangerStorageKey: localStorage.getItem(cashChangerStorageKey),
      },
    })
    const now = dayjs().unix()

    let reservationIds = params.state.selectReservation?.selectedReservationIds || []
    if (!reservationIds.length) {
      const reservationData = {
        hotelId: state.hotelId,
        beginDate: now,
      }
      const { reservationId } = await api.selfCheckinCreateReservation(reservationData)
      reservationIds = [reservationId]
    }

    if (params.state.checkinCustomize?.assignSpaces.length && params.state.selectReservation?.selectedReservationAndRooms.length) {
      try {
        await api.FixSpaceReservationFromSelfCI({
          hotelId: state.hotelId,
          reservationIdWithAssignIds: params.state.selectReservation.selectedReservationAndRooms,
        })
      } catch (e) {
        reportError(e)
      }
    }

    let guest = {} as any
    if (isAccommodationInput(params.state.accommodationInfo)) {
      guest = {
        name: `${params.state.accommodationInfo!.lastName} ${params.state.accommodationInfo!.firstName}`,
        nameKana:
          params.state.accommodationInfo!.nationality === 'japanese' && params.language === 'ja'
            ? `${params.state.accommodationInfo!.lastNameFurigana} ${params.state.accommodationInfo!.firstNameFurigana}`
            : '',
        birthDate: params.state.accommodationInfo!.dateOfBirth,
        nationality: params.state.accommodationInfo!.nationality == 'japanese' ? 'JPN' : params.state.uploadLicense?.nationality || 'NJP',
        gender: params.state.accommodationInfo!.gender,
        postalCode: params.state.accommodationInfo!.nationality == 'japanese' ? params.state.accommodationInfo!.postCode : '',
        address: params.state.accommodationInfo!.address,
        telephone: params.state.accommodationInfo!.phoneNumber,
        email: params.state.accommodationInfo!.mail,
        occupation: params.state.accommodationInfo!.occupation,
        otherOccupation:
          params.state.accommodationInfo?.occupation === OccupationType.Other ? params.state.accommodationInfo!.otherOccupation : null,
        company: isEmployee(params.state.accommodationInfo?.occupation) ? params.state.accommodationInfo!.company : '',
        totalMember: params.state.accommodationInfo!.accompany.length + 1,
      }
    } else {
      guest = {
        name: `${params.state.accommodationInfo!.lastName} ${params.state.accommodationInfo!.firstName}`,
        nationality: params.state.accommodationInfo!.nationality == 'japanese' ? 'JPN' : params.state.uploadLicense?.nationality || 'NJP',
        totalMember: params.state.accommodationInfo!.accompany.length + 1,
      }
    }
    const checkinData = {
      hotelId: params.state.hotelId,
      reservationIds,
      checkin: {
        approvedStatus: 0,
        accompany: params.state.accommodationInfo?.accompany || [],
        guest,
        checkinTime: now,
        userCheckinTime: now,
        nextPlaceToStay: '自宅',
        previousPlaceToStay: '自宅',
        jpnGtdStatus: 0,
      },
      reservationIdWithAssignIds: params.state.selectReservation?.selectedReservationAndRooms || [],
      checkinMemo: params.state.checkinCustomize?.checkinMemo || '',
    }
    if (params.state.confirm?.isDeliveryConfirm !== undefined) {
      Object.assign(checkinData, { deliveryAgreementStatus: params.state.confirm.isDeliveryConfirm ? 'deliverable' : 'nonDeliverable' })
    }
    const checkinResponseInfo = await api.selfCheckinForReservations(checkinData)

    try {
      if (params.state.uploadLicense?.identificationFrontImage) {
        await api.selfCheckinUploadIdentificationImages({
          hotelId: params.state.hotelId,
          identification: {
            checkinId: checkinResponseInfo.checkinId,
            images: buildJapaneseIdentityDocumentImages(params.state.uploadLicense),
          },
        })
      } else if (params.state.uploadLicense?.representativePassportImage) {
        const identificationImageData = {
          hotelId: params.state.hotelId,
          identification: {
            checkinId: checkinResponseInfo.checkinId,
            images: [
              { isDriversLicense: 0, objectKey: params.state.uploadLicense.representativePassportObjectKey, name: '代表者パスポート' },
            ],
          },
        }
        params.state.uploadLicense.companionImageArray?.forEach(companionImg =>
          identificationImageData.identification.images.push({
            isDriversLicense: 0,
            objectKey: companionImg.objectKey,
            name: '同行者パスポート',
          }),
        )
        await api.selfCheckinUploadIdentificationImages(identificationImageData)
      }
    } catch (e) {
      reportError(e)
    }

    let reception: SuccessMailType['reception'] = undefined
    if (params.paymentResult && params.state.confirm?.payment) {
      const toPaymentMethodInput = (
        paymentResult: (PaymentResultByCredit | PaymentResultByCash) & { deviceType?: DeviceType },
      ): PaymentMethodInput[] => {
        if (paymentResult.kind === 'cash') return [{ method: 'cash' }]
        if (paymentResult.deviceType === DeviceType.PaidQr) return [{ method: 'other', otherName: paymentResult.cardBrand }]
        return [{ method: 'credit_card' }]
      }
      reception = {
        language: params.language,
        number: params.paymentResult.orderId || '',
        date: dayjs().format('YYYY-MM-DD HH:mm'),
        paymentAmount: `${params.paymentResult.amount}`,
        name: params.state.confirm.payment.receiptName || '',
        paymentMethods: toPaymentMethodInput(params.paymentResult),
        reservations: params.state.confirm!.payment!.usage.reservations.map(reservation => ({
          useAmount: reservation.totalPrice,
          paymentAmount: reservation.totalDiscount,
          internalDiscount: reservation.totalDiscount,
        })),
        ...(params.paymentResult.kind === 'credit'
          ? {
              paymentMethod: params.paymentResult.cardBrand,
            }
          : {
              paymentMethod: '現金',
              paymentMethodEn: 'Cash',
            }),
      }
    }
    try {
      await api.sendMailSelfCheckin({
        hotelId: params.state.hotelId,
        checkinId: checkinResponseInfo.checkinId,
        checkinMemo: params.state.checkinCustomize?.checkinMemo || '',
        checkinDateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        guestEmail: checkinData.checkin.guest.email,
        guestName: checkinData.checkin.guest.name,
        reception,
        assignSpaces: state.checkinCustomize?.assignSpaces,
        reservationIdWithAssignIds: params.state.selectReservation?.selectedReservationAndRooms || [],
        hasPrinter: !!localStorage.getItem(checkinEposPrinterIdStorage),
      })
    } catch (e) {
      reportError(e)
    }

    const selectedReservations = params.state.selectReservation?.selectedReservations || []
    const memosByLang = selectedReservations.reduce<Partial<Record<string, string[]>>>((acc, r) => {
      if (!r.checkinGuidanceMemo) return acc
      ;(Object.entries(r.checkinGuidanceMemo) as [string, string][]).forEach(([lang, text]) => {
        if (text) {
          acc[lang] = [...(acc[lang] || []), text]
        }
      })
      return acc
    }, {})
    const checkinGuidanceMemo = Object.keys(memosByLang).length > 0 ? memosByLang : undefined

    saveState({
      _type: 'selfCheckIn',
      hotelId: params.state.hotelId,
      setting: params.state.setting,
      complete: {
        hotelId: params.state.hotelId,
        checkinId: checkinResponseInfo.checkinId,
        uploadReservationUuid: pickUploadReservationUuid(
          params.state.selectReservation?.selectedReservations,
          dayjs().format('YYYY-MM-DD'),
        ),
        basicInfo: params.state.setting!.basicInfo,
        isFrontDescGuidance: params.state.setting!.accommodation.isFrontDescGuidance,
        paymentMethod: params.selectedPaymentMethod,
        confirmUsageTotalAmount: params.state.confirm!.payment?.usage.totalAmount || 0,
        payment: params.paymentResult
          ? {
              receiptName: params.state.confirm!.payment?.receiptName || '',
              paymentAmount: params.paymentResult.amount,
              totalAmountAt10Per: params.paymentResult.totalAmountAt10Per,
              totalAmountAt8Per: params.paymentResult.totalAmountAt8Per,
              totalTaxAt10Per: params.paymentResult.totalTaxAt10Per,
              totalTaxAt8Per: params.paymentResult.totalTaxAt8Per,
              deviceType: params.paymentResult.deviceType,
              brandName: params.paymentResult.kind === 'credit' ? params.paymentResult.cardBrand : '',
              salesSlip: params.paymentResult.salesSlip,
            }
          : undefined,
        selectedReservationAndRooms: params.state.selectReservation?.selectedReservationAndRooms || [],
        checkinGuidanceMemo,
      },
    })
  }

  const stateErrorHandling = <K extends keyof SelfCheckInState>(
    state: SelfCheckInState | undefined,
    key: K,
  ): state is RequiredOnly<SelfCheckInState, K> => {
    const hotelIdState = state?.hotelId || sessionStorage.getItem('selfci.hotelId')
    if (!hotelIdState) {
      reportError('Undefined hotelId state')
      signout()
      history.replace({ pathname: '/' })
      return false
    }
    if (!state || !state[key]) {
      reportError('Undefined state')
      api.debugState('stateErrorHandling', {
        selfcheckinstate: sessionStorage.getItem('selfcheckinstate') || '{}',
        state,
        key,
      })
      history.replace({ pathname: '/self-checkin', search: `?hotelId=${hotelIdState}` })
      return false
    }
    return true
  }

  return { state, saveState, clearState, initializeState, doCheckIn, stateErrorHandling }
}
